import { motion } from "framer-motion";
import { Instagram, Star, ArrowUpRight } from "lucide-react";

const TikTokIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.9 2.9 0 0 1-2.88 2.5 2.9 2.9 0 1 1 2.88-3.38V8.75a6.35 6.35 0 0 0 6.33 5.93v-3.4a4.85 4.85 0 0 1-1.11-.13V6.69h2Z" transform="translate(-1 1)" />
    </svg>
);

const STATS = [
    { label: "Abonnés Instagram & TikTok", value: "10K+" },
    { label: "Assiettes servies / mois", value: "6 500" },
    { label: "Note moyenne Google", value: "4.8 ★" },
];

const SOCIALS = [
    {
        name: "Instagram",
        handle: "@petitveau.athismons",
        testid: "social-instagram-link",
        href: "https://instagram.com",
        icon: Instagram,
        desc: "Les coulisses de la braise, les assiettes du jour et les nouveautés.",
    },
    {
        name: "TikTok",
        handle: "@petitveau.athismons",
        testid: "social-tiktok-link",
        href: "https://tiktok.com",
        icon: TikTokIcon,
        desc: "Le feu de bois en vidéo : grillades, sauces maison et behind the fire.",
    },
];

export default function SocialGrid() {
    return (
        <section
            id="socials"
            data-testid="social-section"
            className="relative bg-[#0d0d0d] py-24 sm:py-32"
        >
            <div className="pointer-events-none absolute right-0 top-1/4 h-[300px] w-[300px] rounded-full bg-rose-600/10 blur-[130px]" />
            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="font-mono2 mb-4 text-[11px] uppercase tracking-[0.3em] text-rose-400"
                >
                    La Communauté
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="font-display max-w-3xl text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl"
                >
                    Le feu fait du <span className="text-rose-500">bruit.</span>
                </motion.h2>

                <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-3">
                    {STATS.map((s, i) => (
                        <motion.div
                            key={s.label}
                            data-testid="social-stat-card"
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: i * 0.12 }}
                            className="rounded-2xl border border-white/10 bg-[#18181B] p-8 text-center transition-shadow duration-500 hover:shadow-[0_0_30px_rgba(185,28,28,0.2)]"
                        >
                            <p className="font-display text-4xl font-black tracking-tight text-rose-500 sm:text-5xl">
                                {s.value}
                            </p>
                            <p className="font-mono2 mt-3 text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                                {s.label}
                            </p>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                    {SOCIALS.map((s, i) => (
                        <motion.a
                            key={s.name}
                            href={s.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={s.testid}
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: 0.15 + i * 0.12 }}
                            whileHover={{ y: -6 }}
                            className="group rounded-2xl border border-white/10 bg-[#18181B] p-8 transition-colors duration-300 hover:border-rose-500/40"
                        >
                            <div className="flex items-center justify-between">
                                <span className="grid h-12 w-12 place-items-center rounded-xl bg-rose-600/15 text-rose-500 transition-colors duration-300 group-hover:bg-rose-600 group-hover:text-white">
                                    <s.icon className="h-5 w-5" />
                                </span>
                                <ArrowUpRight className="h-5 w-5 text-zinc-600 transition-colors duration-300 group-hover:text-rose-400" />
                            </div>
                            <h3 className="font-display mt-6 text-xl font-black uppercase tracking-tight">
                                {s.name}
                            </h3>
                            <p className="font-mono2 mt-1 text-xs tracking-wider text-rose-400">
                                {s.handle}
                            </p>
                            <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.desc}</p>
                        </motion.a>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mt-10 flex items-center justify-center gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/5 px-6 py-5"
                >
                    <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
                    <p className="text-sm text-zinc-300 sm:text-base">
                        « On sent la braise dès la première bouchée. La bavette est incroyable. » —{" "}
                        <span className="font-semibold text-zinc-100">Avis Google vérifié</span>
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
